import {FC, Fragment, useState} from 'react'
import {HttpGetSignatureSheetsResponse} from '../../http/accesses.http'
import {StyledAccessesValidatorListItemStatusSquare, StyledAccordionItemContainer} from './style'
import {Text} from '@/components/ui/text/Text'
import {
    AlertCircleIcon,
    CheckIcon,
    Pencil01Icon,
    RefreshCw01Icon,
    SlashCircle01Icon,
    Trash01Icon,
    XCloseIcon
} from '@/components/ui/icon/Icon'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {formatLocaleDate} from '@/dayjs'
import {useTranslation} from '@/translations/i18n'
import {accessGroupNameEnumToLabel, eventTypeMapToLabel} from '@/features/agenda/utils'
import {Button} from '@/components/ui/button/Button'
import {projectSectorToLabel} from '@/features/projects/utils'
import {EditAbsenceDialog} from '../edit-absence-dialog/EditAbsenceDialog'
import {EditAccessDialog} from '../edit-access-dialog/EditAccessDialog'
import {DeleteAccessModal} from '../accesses-other-action-menu/DeleteAccessModal'
import {DiscardAccessConfirmModal} from '../discard-access-button/DiscardAccessConfirmModal'
import {accessesStatusToLabel, accessTypeToLabel} from '../../utils'
import {useTheme} from 'styled-components'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useValidateAccesses} from '../../queries/useValidateAccesses'
import {QUERY_KEYS} from '@/queryClient'
import {toast} from '@/utilities/toast/toast'
import {useUpdateAccess} from '../../queries/useUpdateAccess'
import {useQueryClient} from '@tanstack/react-query'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {invalidateAccessesTableOnly, updateAccessStatusInSignatureSheetsCache} from '../../cacheUtils'

type AccessesValidatorListItemAccessProps = {
    access: HttpGetSignatureSheetsResponse[0]['accesses'][0]
}

export const AccessesValidatorListItemAccess: FC<AccessesValidatorListItemAccessProps> = ({access}) => {
    const {t} = useTranslation()
    const [isDeleteAccessModalOpen, setIsDeleteAccessModalOpen] = useState(false)
    const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false)
    const {palette} = useTheme()
    const queryClient = useQueryClient()
    const {mutate: validateAccess, isPending: isValidating} = useValidateAccesses(
        {
            onSuccess: () => {
                toast.success(t('accesses:access_validated_success', {count: 1}))
            }
        },
        {skipSignatureSheetsRefetch: true}
    )
    const {mutate: updateAccess, isPending: isUpdating} = useUpdateAccess(
        {accessId: access.id},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
                toast.success(t('accesses:access_invalidated_success'))
            }
        }
    )
    const {mutate: discardAccess, isPending: isDiscarding} = useUpdateAccess(
        {accessId: access.id},
        {
            onSuccess: () => {
                invalidateAccessesTableOnly(queryClient)
                updateAccessStatusInSignatureSheetsCache(queryClient, access.id, 'discarded')
                toast.success(t('accesses:access_discarded_success', {count: 1}))
                setIsDiscardConfirmOpen(false)
            }
        }
    )
    const {mutate: restoreAccess, isPending: isRestoring} = useUpdateAccess(
        {accessId: access.id},
        {
            onSuccess: () => {
                invalidateAccessesTableOnly(queryClient)
                updateAccessStatusInSignatureSheetsCache(queryClient, access.id, 'to_validate')
                toast.success(t('accesses:restore_discarded_access_success'))
            }
        }
    )

    return (
        <Fragment>
            <StyledAccordionItemContainer>
                <span aria-hidden="true" />
                <Text size="textSm" palette="neutral" shade="600">
                    {formatLocaleDate(access.event.date, 'DD/MM/YYYY')}
                </Text>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.sector ? t(projectSectorToLabel[access.sector]) : '-'}
                </Text>
                <Flexbox gap={1} direction="column">
                    {access.type === 'absence' && (
                        <Flexbox gap={1}>
                            <Text size="textSm" palette="neutral" shade="600">
                                {t(accessTypeToLabel[access.type])}
                            </Text>
                            {access.event.communicatedOnTime === null && (
                                <Tooltip variant="light" content={t('accesses:absence_not_communicated')}>
                                    <AlertCircleIcon color={palette.orange['500']} size={15} />
                                </Tooltip>
                            )}
                        </Flexbox>
                    )}
                    <Text size="textSm" palette="neutral" weight="500" shade="600">
                        {access.event.eventConsultants?.[0]?.accessType?.groupName
                            ? t(accessGroupNameEnumToLabel[access.event.eventConsultants?.[0]?.accessType?.groupName])
                            : '-'}
                    </Text>
                </Flexbox>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.event.eventConsultants?.[0]?.accessType?.accessType
                        ? t(eventTypeMapToLabel[access.event.eventConsultants?.[0]?.accessType?.accessType])
                        : '-'}
                </Text>
                <Text size="textSm" palette="neutral" shade="600">
                    {access.remappedSiarAccessType ?? '-'}
                </Text>
                <Flexbox align="center" gap={1}>
                    <Text size="textSm" palette="neutral" shade="600">
                        {access.type === 'absence'
                            ? access.event.communicatedOnTime === null
                                ? `${t('accesses:absence_type')}:`
                                : `${t('accesses:absence_type')}: ${
                                      access.event.communicatedOnTime
                                          ? t('commons:over_48_hours')
                                          : t('commons:within_48_hours')
                                  }`
                            : `${t('accesses:report')}: ${
                                  !access.medicalRecordReport
                                      ? t('commons:missing')
                                      : access.medicalRecordReport.status === 'signed'
                                        ? t('accesses:report_signed')
                                        : t('commons:draft')
                              }`}
                    </Text>
                    {access.type === 'absence' && access.event.communicatedOnTime === null && (
                        <Tooltip variant="light" content={t('accesses:absence_not_communicated')}>
                            <AlertCircleIcon color={palette.orange['500']} size={15} />
                        </Tooltip>
                    )}
                </Flexbox>
                <Flexbox align="center" gap={1} style={{marginLeft: 12}}>
                    <StyledAccessesValidatorListItemStatusSquare $status={access.status} />
                    <Text size="textSm" palette="neutral" shade="600">
                        {t(accessesStatusToLabel[access.status])}
                    </Text>
                </Flexbox>
                <Flexbox gap={1} fullWidth justify="end">
                    {(access.status === 'to_validate' || access.status === 'validated') && (
                        <Tooltip
                            variant="light"
                            content={t('accesses:discard_access', {count: 1})}
                            isOpenOnClickEnabled={false}
                            triggerProps={{asChild: true}}
                        >
                            <Button
                                onClick={() => setIsDiscardConfirmOpen(true)}
                                variant="ghost"
                                size="xs"
                                shape="square"
                                disabled={isDiscarding}
                            >
                                {isDiscarding ? <Spinner /> : <SlashCircle01Icon />}
                            </Button>
                        </Tooltip>
                    )}
                    {access.status === 'discarded' && (
                        <Tooltip
                            variant="light"
                            content={t('accesses:restore_discarded_access')}
                            isOpenOnClickEnabled={false}
                            triggerProps={{asChild: true}}
                        >
                            <Button
                                onClick={() => restoreAccess({status: 'to_validate'})}
                                variant="ghost"
                                size="xs"
                                shape="square"
                                disabled={isRestoring}
                            >
                                {isRestoring ? <Spinner /> : <RefreshCw01Icon />}
                            </Button>
                        </Tooltip>
                    )}
                    {access.status == 'to_validate'
                        ? !(access.type === 'absence' && access.event.communicatedOnTime == null) && (
                              <Tooltip
                                  variant="light"
                                  content={t('accesses:validate_access', {count: 1})}
                                  isOpenOnClickEnabled={false}
                                  triggerProps={{asChild: true}}
                              >
                                  <Button
                                      onClick={() => validateAccess({accessIds: [access.id]})}
                                      variant="ghost"
                                      size="xs"
                                      shape="square"
                                      disabled={isValidating}
                                  >
                                      {isValidating ? <Spinner /> : <CheckIcon />}
                                  </Button>
                              </Tooltip>
                          )
                        : access.status == 'validated' && (
                              <Tooltip
                                  variant="light"
                                  content={t('accesses:invalidate_access')}
                                  isOpenOnClickEnabled={false}
                                  triggerProps={{asChild: true}}
                              >
                                  <Button
                                      onClick={() => updateAccess({status: 'to_validate'})}
                                      variant="ghost"
                                      size="xs"
                                      shape="square"
                                      disabled={isUpdating}
                                  >
                                      {isUpdating ? <Spinner /> : <XCloseIcon />}
                                  </Button>
                              </Tooltip>
                          )}
                    {access.type === 'access' && (
                        <EditAccessDialog
                            access={access}
                            trigger={
                                <div>
                                    <Tooltip
                                        variant="light"
                                        content={t('commons:edit')}
                                        isOpenOnClickEnabled={false}
                                        triggerProps={{asChild: true}}
                                    >
                                        <Button
                                            disabled={access.status === 'validated' || access.status === 'discarded'}
                                            variant="ghost"
                                            size="xs"
                                            shape="square"
                                        >
                                            <Pencil01Icon />
                                        </Button>
                                    </Tooltip>
                                </div>
                            }
                        />
                    )}
                    {access.type === 'absence' && (
                        <EditAbsenceDialog
                            access={access}
                            trigger={
                                <div>
                                    <Tooltip
                                        variant="light"
                                        content={t('commons:edit')}
                                        isOpenOnClickEnabled={false}
                                        triggerProps={{asChild: true}}
                                    >
                                        <Button
                                            disabled={access.status === 'validated' || access.status === 'discarded'}
                                            variant="ghost"
                                            size="xs"
                                            shape="square"
                                        >
                                            <Pencil01Icon />
                                        </Button>
                                    </Tooltip>
                                </div>
                            }
                        />
                    )}
                    <Tooltip variant="light" content={t('commons:delete')}>
                        <Button
                            variant="ghost"
                            size="xs"
                            shape="square"
                            onClick={() => setIsDeleteAccessModalOpen(true)}
                        >
                            <Trash01Icon />
                        </Button>
                    </Tooltip>
                </Flexbox>
            </StyledAccordionItemContainer>
            {isDeleteAccessModalOpen && (
                <DeleteAccessModal access={access} onClose={() => setIsDeleteAccessModalOpen(false)} />
            )}
            {isDiscardConfirmOpen && (
                <DiscardAccessConfirmModal
                    count={1}
                    isPending={isDiscarding}
                    onConfirm={() => discardAccess({status: 'discarded'})}
                    onClose={() => setIsDiscardConfirmOpen(false)}
                />
            )}
        </Fragment>
    )
}
